import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/seo/SEOHead';
import SchemaOrg from '../../components/seo/SchemaOrg';
import Navbar from '../../components/feature/Navbar';
import FeedbackBanner from '../../components/feature/FeedbackBanner';
import Footer from '../../components/feature/Footer';

export default function ArticleEscritorioContabil() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEOHead 
        title="Como Administrar um Escritório Contábil com Sucesso - Ativo News"
        description="Guia completo com estratégias práticas para gestão eficiente de escritórios contábeis, incluindo tecnologia, processos e relacionamento com clientes."
        keywords="escritório contábil, gestão contábil, administração contábil, tecnologia contábil"
        canonical="https://ativonews.com.br/artigo/como-administrar-escritorio-contabil-sucesso"
      />
      <SchemaOrg type="Article" data={{
        headline: "Como Administrar um Escritório Contábil com Sucesso",
        description: "Guia completo com estratégias práticas para gestão eficiente de escritórios contábeis, incluindo tecnologia, processos e relacionamento com clientes.",
        author: "Dra. Ana Paula Costa",
        datePublished: "2025-01-18",
        image: "https://public.readdy.ai/ai/img_res/242dd16e73f170bab809cc18dd846913.jpg"
      }} />
      
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-[#F8FAFC] to-white py-16 border-b border-gray-100">
          <div className="max-w-4xl mx-auto px-6">
            <Link 
              to="/categoria/ativo-fixo-patrimonio"
              className="inline-block px-4 py-2 bg-[#1E40AF] text-white text-sm font-semibold rounded-lg mb-6 hover:bg-[#1E3A8A] transition-colors uppercase tracking-wide"
            >
              Ativo Fixo &amp; Patrimônio
            </Link>
            
            <h1 className="text-4xl lg:text-5xl font-bold text-[#0F172A] leading-tight mb-6">
              Como Administrar um Escritório Contábil de Sucesso: Gestão, Pessoas e Processos
            </h1>
            
            <div className="flex items-center gap-6 text-[#64748B] text-sm mb-8">
              <div className="flex items-center gap-2">
                <i className="ri-user-line text-lg"></i>
                <span className="font-medium">Roberto Andrade</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-calendar-line text-lg"></i>
                <span>15 de Janeiro de 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-time-line text-lg"></i>
                <span>12 min de leitura</span>
              </div>
            </div>

            <div className="bg-[#FEF3C7] border-l-4 border-[#F59E0B] p-6 rounded-r-lg">
              <p className="text-[#92400E] leading-relaxed">
                <strong className="font-bold">TL;DR:</strong> Um escritório contábil de sucesso não é o que "trabalha muito", mas o que entrega com previsibilidade, qualidade e margem. Este guia apresenta estratégias práticas para estruturar processos (BPM), liderar pessoas, medir KPIs, implementar tecnologia e garantir segurança da informação. Inclui checklist de 30 dias para organizar seu escritório e FAQ com dúvidas frequentes sobre gestão contábil.
              </p>
            </div>
          </div>
        </div>

        {/* Article Content */}
        <div className="max-w-4xl mx-auto px-6 mt-12">
          {/* Sumário */}
          <div className="bg-[#F8FAFC] border border-gray-200 rounded-xl p-8 mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-6 pb-3 border-b-2 border-[#1E40AF]">
              Sumário
            </h2>
            <nav className="space-y-3">
              <a href="#definicao" className="block text-[#1E40AF] hover:text-[#1E3A8A] font-medium transition-colors">
                1. O que define um escritório contábil "bem administrado"
              </a>
              <a href="#gestao-processos" className="block text-[#1E40AF] hover:text-[#1E3A8A] font-medium transition-colors">
                2. Gestão por processos (BPM): o motor da produtividade
              </a>
              <a href="#pessoas" className="block text-[#1E40AF] hover:text-[#1E3A8A] font-medium transition-colors">
                3. Pessoas: time, papéis, rotinas e cultura
              </a>
              <a href="#kpis" className="block text-[#1E40AF] hover:text-[#1E3A8A] font-medium transition-colors">
                4. Indicadores (KPIs): como medir e melhorar
              </a>
              <a href="#tecnologia" className="block text-[#1E40AF] hover:text-[#1E3A8A] font-medium transition-colors">
                5. Tecnologia, qualidade e segurança (LGPD)
              </a>
              <a href="#checklist" className="block text-[#1E40AF] hover:text-[#1E3A8A] font-medium transition-colors">
                6. Checklist prático para organizar o escritório em 30 dias
              </a>
              <a href="#checklist-detalhado" className="block text-[#1E40AF] hover:text-[#1E3A8A] font-medium transition-colors">
                7. Checklist operacional detalhado (por área)
              </a>
              <a href="#faq" className="block text-[#1E40AF] hover:text-[#1E3A8A] font-medium transition-colors">
                8. Perguntas Frequentes (FAQ)
              </a>
            </nav>
          </div>

          {/* Section 1 */}
          <section id="definicao" className="mb-16 scroll-mt-24">
            <h2 className="text-3xl font-bold text-[#0F172A] mb-6 pb-3 border-b-2 border-[#1E40AF]">
              O que define um escritório contábil "bem administrado"
            </h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-[#475569] leading-relaxed mb-6">
                Um escritório contábil de sucesso não é o que "trabalha muito"; é o que <strong className="text-[#0F172A]">entrega com previsibilidade, qualidade e margem</strong>, sem depender de heróis apagando incêndio. Na prática, isso significa ter processos claros, responsabilidades definidas, métricas de desempenho e um sistema de melhoria contínua — em vez de depender apenas do conhecimento tácito de algumas pessoas.
              </p>
              <p className="text-[#475569] leading-relaxed mb-6">
                O ponto central: <strong className="text-[#0F172A]">contabilidade é prestação de serviço recorrente e regulada</strong>. Logo, padronização e monitoramento são vantagem competitiva — inclusive para reduzir retrabalho, atrasos e riscos.
              </p>
              <div className="bg-[#DBEAFE] border-l-4 border-[#1E40AF] p-6 rounded-r-lg my-8">
                <p className="text-[#1E3A8A] leading-relaxed">
                  <strong className="font-bold">💡 Insight:</strong> Escritórios que dependem de "pessoas-chave" para resolver tudo têm um problema estrutural. A meta é criar um sistema onde a qualidade não depende de quem está executando.
                </p>
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section id="gestao-processos" className="mb-16 scroll-mt-24">
            <h2 className="text-3xl font-bold text-[#0F172A] mb-6 pb-3 border-b-2 border-[#1E40AF]">
              Gestão por processos (BPM): o motor da produtividade
            </h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-[#475569] leading-relaxed mb-6">
                A gestão de processos (muitas vezes chamada de <strong className="text-[#0F172A]">BPM – Business Process Management</strong>) é um conjunto de ações para identificar, desenvolver, documentar, monitorar e controlar os processos do escritório, aumentando padronização e qualidade das entregas.
              </p>

              <h3 className="text-2xl font-bold text-[#0F172A] mt-10 mb-4">
                Quais processos mapear primeiro (prioridade real)
              </h3>
              <p className="text-[#475569] leading-relaxed mb-4">
                Comece pelos fluxos que mais geram risco e retrabalho:
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3 text-[#475569]">
                  <i className="ri-checkbox-circle-fill text-[#10B981] text-xl mt-1 flex-shrink-0"></i>
                  <span><strong className="text-[#0F172A]">Onboarding de cliente</strong> (documentos, acessos, procurações, regime, responsabilidades)</span>
                </li>
                <li className="flex items-start gap-3 text-[#475569]">
                  <i className="ri-checkbox-circle-fill text-[#10B981] text-xl mt-1 flex-shrink-0"></i>
                  <span><strong className="text-[#0F172A]">Rotina fiscal</strong> (apurações, entregas, conferências)</span>
                </li>
                <li className="flex items-start gap-3 text-[#475569]">
                  <i className="ri-checkbox-circle-fill text-[#10B981] text-xl mt-1 flex-shrink-0"></i>
                  <span><strong className="text-[#0F172A]">DP/Folha</strong> (pontos críticos de prazo)</span>
                </li>
                <li className="flex items-start gap-3 text-[#475569]">
                  <i className="ri-checkbox-circle-fill text-[#10B981] text-xl mt-1 flex-shrink-0"></i>
                  <span><strong className="text-[#0F172A]">Fechamento contábil</strong> (conciliações, validações, evidências)</span>
                </li>
                <li className="flex items-start gap-3 text-[#475569]">
                  <i className="ri-checkbox-circle-fill text-[#10B981] text-xl mt-1 flex-shrink-0"></i>
                  <span><strong className="text-[#0F172A]">Atendimento e solicitações</strong> (triagem, SLA, escalonamento)</span>
                </li>
              </ul>

              <h3 className="text-2xl font-bold text-[#0F172A] mt-10 mb-4">
                As 5 fases para implementar gestão de processos
              </h3>
              <p className="text-[#475569] leading-relaxed mb-6">
                Use um ciclo simples (e iterativo):
              </p>
              <div className="grid md:grid-cols-5 gap-4 mb-8">
                <div className="bg-[#F8FAFC] border border-gray-200 rounded-lg p-4 text-center">
                  <div className="w-12 h-12 bg-[#1E40AF] text-white rounded-full flex items-center justify-center mx-auto mb-3 text-xl font-bold">1</div>
                  <p className="text-sm font-semibold text-[#0F172A]">Planejamento</p>
                </div>
                <div className="bg-[#F8FAFC] border border-gray-200 rounded-lg p-4 text-center">
                  <div className="w-12 h-12 bg-[#1E40AF] text-white rounded-full flex items-center justify-center mx-auto mb-3 text-xl font-bold">2</div>
                  <p className="text-sm font-semibold text-[#0F172A]">Modelagem</p>
                </div>
                <div className="bg-[#F8FAFC] border border-gray-200 rounded-lg p-4 text-center">
                  <div className="w-12 h-12 bg-[#1E40AF] text-white rounded-full flex items-center justify-center mx-auto mb-3 text-xl font-bold">3</div>
                  <p className="text-sm font-semibold text-[#0F172A]">Implementação</p>
                </div>
                <div className="bg-[#F8FAFC] border border-gray-200 rounded-lg p-4 text-center">
                  <div className="w-12 h-12 bg-[#1E40AF] text-white rounded-full flex items-center justify-center mx-auto mb-3 text-xl font-bold">4</div>
                  <p className="text-sm font-semibold text-[#0F172A]">Monitoramento</p>
                </div>
                <div className="bg-[#F8FAFC] border border-gray-200 rounded-lg p-4 text-center">
                  <div className="w-12 h-12 bg-[#1E40AF] text-white rounded-full flex items-center justify-center mx-auto mb-3 text-xl font-bold">5</div>
                  <p className="text-sm font-semibold text-[#0F172A]">Otimização</p>
                </div>
              </div>
              <p className="text-[#475569] leading-relaxed mb-6">
                <strong className="text-[#0F172A]">Dica prática:</strong> documente "o jeito certo de fazer" em checklists e modelos. Isso reduz dependência de pessoas específicas e acelera treinamento.
              </p>

              <h3 className="text-2xl font-bold text-[#0F172A] mt-10 mb-4">
                Entregas mínimas de processo (o "kit" que funciona)
              </h3>
              <p className="text-[#475569] leading-relaxed mb-4">
                Para cada rotina, defina:
              </p>
              <div className="bg-white border-2 border-[#1E40AF] rounded-xl p-6 mb-8">
                <ul className="space-y-4">
                  <li className="flex items-start gap-3 text-[#475569]">
                    <i className="ri-arrow-right-circle-fill text-[#1E40AF] text-xl mt-1 flex-shrink-0"></i>
                    <span><strong className="text-[#0F172A]">Entrada:</strong> o que precisa chegar completo</span>
                  </li>
                  <li className="flex items-start gap-3 text-[#475569]">
                    <i className="ri-arrow-right-circle-fill text-[#1E40AF] text-xl mt-1 flex-shrink-0"></i>
                    <span><strong className="text-[#0F172A]">Responsável:</strong> dono do processo</span>
                  </li>
                  <li className="flex items-start gap-3 text-[#475569]">
                    <i className="ri-arrow-right-circle-fill text-[#1E40AF] text-xl mt-1 flex-shrink-0"></i>
                    <span><strong className="text-[#0F172A]">Passo a passo:</strong> checklist</span>
                  </li>
                  <li className="flex items-start gap-3 text-[#475569]">
                    <i className="ri-arrow-right-circle-fill text-[#1E40AF] text-xl mt-1 flex-shrink-0"></i>
                    <span><strong className="text-[#0F172A]">Padrão de qualidade:</strong> o que validar antes de entregar</span>
                  </li>
                  <li className="flex items-start gap-3 text-[#475569]">
                    <i className="ri-arrow-right-circle-fill text-[#1E40AF] text-xl mt-1 flex-shrink-0"></i>
                    <span><strong className="text-[#0F172A]">Prazo/SLA:</strong> tempo esperado de execução</span>
                  </li>
                  <li className="flex items-start gap-3 text-[#475569]">
                    <i className="ri-arrow-right-circle-fill text-[#1E40AF] text-xl mt-1 flex-shrink-0"></i>
                    <span><strong className="text-[#0F172A]">Saída:</strong> o que o cliente recebe e em qual formato</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section id="pessoas" className="mb-16 scroll-mt-24">
            <h2 className="text-3xl font-bold text-[#0F172A] mb-6 pb-3 border-b-2 border-[#1E40AF]">
              Pessoas: time, papéis, rotinas e cultura
            </h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-[#475569] leading-relaxed mb-6">
                Processo sem gente certa vira "manual que ninguém segue". Três pontos fazem mais diferença:
              </p>

              <h3 className="text-2xl font-bold text-[#0F172A] mt-10 mb-4">
                Papéis claros (para evitar retrabalho e conflitos)
              </h3>
              <p className="text-[#475569] leading-relaxed mb-4">
                Organize responsabilidades por função, por exemplo:
              </p>
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-[#F8FAFC] border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-[#1E40AF] text-white rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-settings-3-line text-xl"></i>
                    </div>
                    <h4 className="font-bold text-[#0F172A]">Líder de Operações</h4>
                  </div>
                  <p className="text-sm text-[#475569]">Garante fluxo e prazos</p>
                </div>
                <div className="bg-[#F8FAFC] border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-[#1E40AF] text-white rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-shield-check-line text-xl"></i>
                    </div>
                    <h4 className="font-bold text-[#0F172A]">Responsável Técnico</h4>
                  </div>
                  <p className="text-sm text-[#475569]">Padrão técnico e validação</p>
                </div>
                <div className="bg-[#F8FAFC] border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-[#1E40AF] text-white rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-customer-service-2-line text-xl"></i>
                    </div>
                    <h4 className="font-bold text-[#0F172A]">CS/Relacionamento</h4>
                  </div>
                  <p className="text-sm text-[#475569]">Solicitações, alinhamento, retenção</p>
                </div>
                <div className="bg-[#F8FAFC] border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-[#1E40AF] text-white rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-database-2-line text-xl"></i>
                    </div>
                    <h4 className="font-bold text-[#0F172A]">Backoffice de Dados</h4>
                  </div>
                  <p className="text-sm text-[#475569]">Cadastros, conferências, integrações</p>
                </div>
              </div>

              <h3 className="text-2xl font-bold text-[#0F172A] mt-10 mb-4">
                Rotinas que criam previsibilidade
              </h3>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3 text-[#475569]">
                  <i className="ri-calendar-check-line text-[#10B981] text-xl mt-1 flex-shrink-0"></i>
                  <span><strong className="text-[#0F172A]">Reunião rápida semanal</strong> (15–20 min): prazos da semana, gargalos, priorização</span>
                </li>
                <li className="flex items-start gap-3 text-[#475569]">
                  <i className="ri-calendar-check-line text-[#10B981] text-xl mt-1 flex-shrink-0"></i>
                  <span><strong className="text-[#0F172A]">Revisão quinzenal de qualidade:</strong> erros recorrentes e ações preventivas</span>
                </li>
                <li className="flex items-start gap-3 text-[#475569]">
                  <i className="ri-calendar-check-line text-[#10B981] text-xl mt-1 flex-shrink-0"></i>
                  <span><strong className="text-[#0F172A]">1:1 mensal com time:</strong> performance, capacitação, carga e dificuldades</span>
                </li>
              </ul>

              <h3 className="text-2xl font-bold text-[#0F172A] mt-10 mb-4">
                Treinamento (o jeito mais rápido de escalar)
              </h3>
              <p className="text-[#475569] leading-relaxed mb-4">
                Defina trilhas por nível de senioridade:
              </p>
              <div className="space-y-4 mb-8">
                <div className="bg-white border-l-4 border-[#10B981] p-5 rounded-r-lg">
                  <h4 className="font-bold text-[#0F172A] mb-2">👶 Júnior</h4>
                  <p className="text-[#475569] text-sm">Execução com checklist + validações básicas</p>
                </div>
                <div className="bg-white border-l-4 border-[#F59E0B] p-5 rounded-r-lg">
                  <h4 className="font-bold text-[#0F172A] mb-2">👔 Pleno</h4>
                  <p className="text-[#475569] text-sm">Análise e revisão</p>
                </div>
                <div className="bg-white border-l-4 border-[#1E40AF] p-5 rounded-r-lg">
                  <h4 className="font-bold text-[#0F172A] mb-2">🎓 Sênior</h4>
                  <p className="text-[#475569] text-sm">Exceções, clientes complexos, melhoria de processos</p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section id="kpis" className="mb-16 scroll-mt-24">
            <h2 className="text-3xl font-bold text-[#0F172A] mb-6 pb-3 border-b-2 border-[#1E40AF]">
              Indicadores (KPIs): como medir e melhorar
            </h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-[#475569] leading-relaxed mb-6">
                <strong className="text-[#0F172A]">KPI</strong> é "Indicador-Chave de Desempenho": mede a eficácia dos processos em relação aos objetivos do programa/área.
              </p>

              <h3 className="text-2xl font-bold text-[#0F172A] mt-10 mb-4">
                Os KPIs mais úteis para escritório contábil
              </h3>
              <p className="text-[#475569] leading-relaxed mb-4">
                Comece simples (poucos, mas bons):
              </p>
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gradient-to-br from-[#DBEAFE] to-white border border-[#1E40AF] rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-[#1E40AF] text-white rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-2xl"></i>
                    </div>
                    <h4 className="font-bold text-[#0F172A]">% Entregas no Prazo</h4>
                  </div>
                  <p className="text-sm text-[#475569]">Por área: fiscal/contábil/DP</p>
                </div>
                <div className="bg-gradient-to-br from-[#FEF3C7] to-white border border-[#F59E0B] rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-[#F59E0B] text-white rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-refresh-line text-2xl"></i>
                    </div>
                    <h4 className="font-bold text-[#0F172A]">Retrabalho</h4>
                  </div>
                  <p className="text-sm text-[#475569]">Quantidade e causa por competência</p>
                </div>
                <div className="bg-gradient-to-br from-[#D1FAE5] to-white border border-[#10B981] rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-[#10B981] text-white rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-customer-service-line text-2xl"></i>
                    </div>
                    <h4 className="font-bold text-[#0F172A]">Tempo de Atendimento</h4>
                  </div>
                  <p className="text-sm text-[#475569]">SLA de solicitações</p>
                </div>
                <div className="bg-gradient-to-br from-[#E0E7FF] to-white border border-[#6366F1] rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-[#6366F1] text-white rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-user-settings-line text-2xl"></i>
                    </div>
                    <h4 className="font-bold text-[#0F172A]">Capacidade por Colaborador</h4>
                  </div>
                  <p className="text-sm text-[#475569]">Carteira vs complexidade</p>
                </div>
                <div className="bg-gradient-to-br from-[#FEE2E2] to-white border border-[#DC2626] rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-[#DC2626] text-white rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-error-warning-line text-2xl"></i>
                    </div>
                    <h4 className="font-bold text-[#0F172A]">Erros Críticos</h4>
                  </div>
                  <p className="text-sm text-[#475569]">Os que geram risco ou penalidade</p>
                </div>
              </div>

              <div className="bg-[#FEF3C7] border-l-4 border-[#F59E0B] p-6 rounded-r-lg my-8">
                <p className="text-[#92400E] leading-relaxed">
                  <strong className="font-bold">⚠️ Importante:</strong> evite "medir tudo". Selecione uma "métrica estrela norte" para guiar evolução e mantenha a estratégia enxuta.
                </p>
              </div>
            </div>
          </section>

          {/* Section 5 */}
          <section id="tecnologia" className="mb-16 scroll-mt-24">
            <h2 className="text-3xl font-bold text-[#0F172A] mb-6 pb-3 border-b-2 border-[#1E40AF]">
              Tecnologia, qualidade e segurança (LGPD)
            </h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-[#475569] leading-relaxed mb-6">
                Escritórios contábeis lidam com dados sensíveis. <strong className="text-[#0F172A]">Segurança da informação</strong> se sustenta em três pilares: <strong className="text-[#0F172A]">confidencialidade, integridade e disponibilidade</strong>.
              </p>

              <h3 className="text-2xl font-bold text-[#0F172A] mt-10 mb-4">
                O mínimo de segurança para um escritório moderno
              </h3>
              <div className="space-y-4 mb-8">
                <div className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#10B981] text-white rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-lock-line text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#0F172A] mb-2">Controle de Acessos por Perfil</h4>
                      <p className="text-sm text-[#475569]">Defina quem vê o quê, evitando exposição desnecessária de dados</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#10B981] text-white rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-folder-line text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#0F172A] mb-2">Padronização de Armazenamento</h4>
                      <p className="text-sm text-[#475569]">Evite "documentos espalhados" em e-mails e pastas pessoais</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#10B981] text-white rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-save-line text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#0F172A] mb-2">Rotina de Backup</h4>
                      <p className="text-sm text-[#475569]">Automatize backups e teste a restauração periodicamente</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#10B981] text-white rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-team-line text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#0F172A] mb-2">Conscientização</h4>
                      <p className="text-sm text-[#475569]">Treinamento do time sobre boas práticas de segurança</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#10B981] text-white rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-file-text-line text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#0F172A] mb-2">Política Interna</h4>
                      <p className="text-sm text-[#475569]">Documente regras de uso e compartilhamento de dados</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#DBEAFE] border-l-4 border-[#1E40AF] p-6 rounded-r-lg my-8">
                <p className="text-[#1E3A8A] leading-relaxed">
                  <strong className="font-bold">🔒 LGPD:</strong> A Lei Geral de Proteção de Dados exige práticas e organização; não é só tecnologia. O escritório precisa tratar a segurança como processo — com dono, rotina e auditoria interna.
                </p>
              </div>
            </div>
          </section>

          {/* Section 6 - Checklist */}
          <section id="checklist" className="mb-16 scroll-mt-24">
            <h2 className="text-3xl font-bold text-[#0F172A] mb-6 pb-3 border-b-2 border-[#1E40AF]">
              Checklist prático para organizar o escritório em 30 dias
            </h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-[#475569] leading-relaxed mb-6">
                Use este roteiro para estruturar seu escritório em um mês:
              </p>

              <div className="space-y-6">
                <div className="bg-gradient-to-r from-[#DBEAFE] to-white border-l-4 border-[#1E40AF] rounded-r-xl p-6">
                  <h3 className="text-xl font-bold text-[#0F172A] mb-4 flex items-center gap-3">
                    <span className="w-8 h-8 bg-[#1E40AF] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
                    Semana 1: Diagnóstico e Priorização
                  </h3>
                  <ul className="space-y-2 ml-11">
                    <li className="flex items-start gap-2 text-[#475569]">
                      <i className="ri-checkbox-blank-circle-fill text-[#1E40AF] text-xs mt-2 flex-shrink-0"></i>
                      <span>Liste os 5 processos mais críticos (maior risco/retrabalho)</span>
                    </li>
                    <li className="flex items-start gap-2 text-[#475569]">
                      <i className="ri-checkbox-blank-circle-fill text-[#1E40AF] text-xs mt-2 flex-shrink-0"></i>
                      <span>Identifique gargalos e dependências de pessoas-chave</span>
                    </li>
                    <li className="flex items-start gap-2 text-[#475569]">
                      <i className="ri-checkbox-blank-circle-fill text-[#1E40AF] text-xs mt-2 flex-shrink-0"></i>
                      <span>Defina 2-3 KPIs prioritários para acompanhar</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-r from-[#D1FAE5] to-white border-l-4 border-[#10B981] rounded-r-xl p-6">
                  <h3 className="text-xl font-bold text-[#0F172A] mb-4 flex items-center gap-3">
                    <span className="w-8 h-8 bg-[#10B981] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
                    Semana 2: Documentação e Papéis
                  </h3>
                  <ul className="space-y-2 ml-11">
                    <li className="flex items-start gap-2 text-[#475569]">
                      <i className="ri-checkbox-blank-circle-fill text-[#10B981] text-xs mt-2 flex-shrink-0"></i>
                      <span>Crie checklists para os 2 processos mais urgentes</span>
                    </li>
                    <li className="flex items-start gap-2 text-[#475569]">
                      <i className="ri-checkbox-blank-circle-fill text-[#10B981] text-xs mt-2 flex-shrink-0"></i>
                      <span>Defina responsáveis claros por área (Operações, Técnico, CS, Backoffice)</span>
                    </li>
                    <li className="flex items-start gap-2 text-[#475569]">
                      <i className="ri-checkbox-blank-circle-fill text-[#10B981] text-xs mt-2 flex-shrink-0"></i>
                      <span>Estabeleça SLAs básicos para atendimento e entregas</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-r from-[#FEF3C7] to-white border-l-4 border-[#F59E0B] rounded-r-xl p-6">
                  <h3 className="text-xl font-bold text-[#0F172A] mb-4 flex items-center gap-3">
                    <span className="w-8 h-8 bg-[#F59E0B] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
                    Semana 3: Rotinas e Monitoramento
                  </h3>
                  <ul className="space-y-2 ml-11">
                    <li className="flex items-start gap-2 text-[#475569]">
                      <i className="ri-checkbox-blank-circle-fill text-[#F59E0B] text-xs mt-2 flex-shrink-0"></i>
                      <span>Implemente reunião semanal de 15-20 min (prazos e gargalos)</span>
                    </li>
                    <li className="flex items-start gap-2 text-[#475569]">
                      <i className="ri-checkbox-blank-circle-fill text-[#F59E0B] text-xs mt-2 flex-shrink-0"></i>
                      <span>Configure planilha ou dashboard simples para acompanhar KPIs</span>
                    </li>
                    <li className="flex items-start gap-2 text-[#475569]">
                      <i className="ri-checkbox-blank-circle-fill text-[#F59E0B] text-xs mt-2 flex-shrink-0"></i>
                      <span>Inicie registro de retrabalhos e erros (causa raiz)</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-r from-[#E0E7FF] to-white border-l-4 border-[#6366F1] rounded-r-xl p-6">
                  <h3 className="text-xl font-bold text-[#0F172A] mb-4 flex items-center gap-3">
                    <span className="w-8 h-8 bg-[#6366F1] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">4</span>
                    Semana 4: Segurança e Melhoria Contínua
                  </h3>
                  <ul className="space-y-2 ml-11">
                    <li className="flex items-start gap-2 text-[#475569]">
                      <i className="ri-checkbox-blank-circle-fill text-[#6366F1] text-xs mt-2 flex-shrink-0"></i>
                      <span>Revise controles de acesso e armazenamento de documentos</span>
                    </li>
                    <li className="flex items-start gap-2 text-[#475569]">
                      <i className="ri-checkbox-blank-circle-fill text-[#6366F1] text-xs mt-2 flex-shrink-0"></i>
                      <span>Documente política básica de segurança e LGPD</span>
                    </li>
                    <li className="flex items-start gap-2 text-[#475569]">
                      <i className="ri-checkbox-blank-circle-fill text-[#6366F1] text-xs mt-2 flex-shrink-0"></i>
                      <span>Agende revisão quinzenal de qualidade (erros recorrentes)</span>
                    </li>
                    <li className="flex items-start gap-2 text-[#475569]">
                      <i className="ri-checkbox-blank-circle-fill text-[#6366F1] text-xs mt-2 flex-shrink-0"></i>
                      <span>Planeje próximos processos a documentar no mês seguinte</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-[#D1FAE5] border-l-4 border-[#10B981] p-6 rounded-r-lg mt-8">
                <p className="text-[#065F46] leading-relaxed">
                  <strong className="font-bold">✅ Resultado esperado:</strong> Ao final de 30 dias, você terá processos documentados, papéis claros, rotinas de acompanhamento e base para escalar com qualidade.
                </p>
              </div>
            </div>
          </section>

          {/* Section 7 - Checklist Operacional Detalhado */}
          <section id="checklist-detalhado" className="mb-16 scroll-mt-24">
            <h2 className="text-3xl font-bold text-[#0F172A] mb-6 pb-3 border-b-2 border-[#1E40AF]">
              Checklist Operacional Detalhado (30 dias)
            </h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-[#475569] leading-relaxed mb-8">
                Tabelas práticas com rotinas, responsáveis, ferramentas e KPIs para implementar em seu escritório:
              </p>

              {/* Tabela 1: Visão Geral por Semana */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold text-[#0F172A] mb-6 flex items-center gap-3">
                  <i className="ri-calendar-line text-[#1E40AF]"></i>
                  Checklist Operacional — Visão Geral (por semana)
                </h3>
                <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-lg">
                  <table className="min-w-full bg-white">
                    <thead className="bg-gradient-to-r from-[#1E40AF] to-[#1E3A8A] text-white">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-bold">Semana</th>
                        <th className="px-6 py-4 text-left text-sm font-bold">Objetivo</th>
                        <th className="px-6 py-4 text-left text-sm font-bold">Entregáveis</th>
                        <th className="px-6 py-4 text-left text-sm font-bold">Responsável</th>
                        <th className="px-6 py-4 text-left text-sm font-bold">Frequência</th>
                        <th className="px-6 py-4 text-left text-sm font-bold">Ferramentas</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr className="hover:bg-[#F8FAFC] transition-colors">
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center justify-center w-8 h-8 bg-[#1E40AF] text-white rounded-full font-bold text-sm">1</span>
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#0F172A]">
                          "Arrumar a casa"<br />(processos e responsabilidades)
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">
                          Mapa de processos por área + SLAs + matriz RACI + calendário fiscal/DP
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">
                          Sócio/Responsável Técnico + Coord. Operações
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-block px-3 py-1 bg-[#DBEAFE] text-[#1E40AF] text-xs font-semibold rounded-full">Diário</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">
                          Notion/Confluence, Google Sheets/Airtable, Trello/ClickUp
                        </td>
                      </tr>
                      <tr className="hover:bg-[#F8FAFC] transition-colors">
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center justify-center w-8 h-8 bg-[#10B981] text-white rounded-full font-bold text-sm">2</span>
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#0F172A]">
                          Controle de prazos<br />e capacidade
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">
                          Esteira de produção por competência + alocação de carteira + fila de atendimento
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">
                          Coord. Operações + líderes de área
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-block px-3 py-1 bg-[#D1FAE5] text-[#10B981] text-xs font-semibold rounded-full">Semanal</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">
                          Kanban (ClickUp/Jira), planilha de capacidade, CRM/Helpdesk
                        </td>
                      </tr>
                      <tr className="hover:bg-[#F8FAFC] transition-colors">
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center justify-center w-8 h-8 bg-[#F59E0B] text-white rounded-full font-bold text-sm">3</span>
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#0F172A]">
                          Qualidade e<br />previsibilidade
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">
                          Rotina de revisão (amostral) + painel de KPIs + padrão de comunicação com cliente
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">
                          Responsável Técnico + Qualidade
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-block px-3 py-1 bg-[#FEF3C7] text-[#F59E0B] text-xs font-semibold rounded-full">Semanal</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">
                          Dashboards (Looker Studio/Power BI), checklists, templates
                        </td>
                      </tr>
                      <tr className="hover:bg-[#F8FAFC] transition-colors">
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center justify-center w-8 h-8 bg-[#6366F1] text-white rounded-full font-bold text-sm">4</span>
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#0F172A]">
                          Otimização e escala
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">
                          Plano de melhoria contínua + automações + treinamento/reciclagem
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">
                          Sócio + Coord. Operações
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-block px-3 py-1 bg-[#E0E7FF] text-[#6366F1] text-xs font-semibold rounded-full">Quinzenal/Mensal</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">
                          Zapier/Make, RPA/robôs do sistema, LMS/treinamentos
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Tabela 2: Checklist Fiscal Detalhado */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold text-[#0F172A] mb-6 flex items-center gap-3">
                  <i className="ri-file-list-3-line text-[#10B981]"></i>
                  Checklist por Área: Fiscal (30 dias)
                </h3>
                <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-lg">
                  <table className="min-w-full bg-white">
                    <thead className="bg-gradient-to-r from-[#10B981] to-[#059669] text-white">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-bold">Dia (janela)</th>
                        <th className="px-6 py-4 text-left text-sm font-bold">Rotina / Check</th>
                        <th className="px-6 py-4 text-left text-sm font-bold">Responsável (R)</th>
                        <th className="px-6 py-4 text-left text-sm font-bold">Aprovador (A)</th>
                        <th className="px-6 py-4 text-left text-sm font-bold">Frequência</th>
                        <th className="px-6 py-4 text-left text-sm font-bold">Ferramentas</th>
                        <th className="px-6 py-4 text-left text-sm font-bold">KPI Principal</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr className="hover:bg-[#F8FAFC] transition-colors">
                        <td className="px-6 py-4">
                          <span className="inline-block px-3 py-1 bg-[#DBEAFE] text-[#1E40AF] text-xs font-bold rounded">D1–D3</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">
                          Padronizar "Kit Fiscal" por regime (Simples/Lucro Presumido/Lucro Real): checklist + prazos + documentos de entrada
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#0F172A]">Líder Fiscal</td>
                        <td className="px-6 py-4 text-sm text-[#475569]">Resp. Técnico</td>
                        <td className="px-6 py-4">
                          <span className="inline-block px-2 py-1 bg-[#FEF3C7] text-[#92400E] text-xs font-semibold rounded">Única + revisão mensal</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">Notion/Confluence + planilha calendário</td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#10B981]">% processos documentados (≥90%)</td>
                      </tr>
                      <tr className="hover:bg-[#F8FAFC] transition-colors">
                        <td className="px-6 py-4">
                          <span className="inline-block px-3 py-1 bg-[#DBEAFE] text-[#1E40AF] text-xs font-bold rounded">D1–D5</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">
                          Criar calendário único de obrigações (federal/estadual/municipal) + responsáveis por cliente
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#0F172A]">Coord. Operações</td>
                        <td className="px-6 py-4 text-sm text-[#475569]">Líder Fiscal</td>
                        <td className="px-6 py-4">
                          <span className="inline-block px-2 py-1 bg-[#FEF3C7] text-[#92400E] text-xs font-semibold rounded">Única + atualização semanal</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">Google Sheets/Airtable</td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#10B981]">% obrigações com dono definido (100%)</td>
                      </tr>
                      <tr className="hover:bg-[#F8FAFC] transition-colors">
                        <td className="px-6 py-4">
                          <span className="inline-block px-3 py-1 bg-[#DBEAFE] text-[#1E40AF] text-xs font-bold rounded">D6–D10</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">
                          Definir "esteira" por competência: Entrada → Conferência → Apuração → Validação → Entrega
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#0F172A]">Líder Fiscal</td>
                        <td className="px-6 py-4 text-sm text-[#475569]">Coord. Operações</td>
                        <td className="px-6 py-4">
                          <span className="inline-block px-2 py-1 bg-[#D1FAE5] text-[#065F46] text-xs font-semibold rounded">Semanal</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">Kanban (ClickUp/Jira)</td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#10B981]">% entregas no prazo (≥95%)</td>
                      </tr>
                      <tr className="hover:bg-[#F8FAFC] transition-colors">
                        <td className="px-6 py-4">
                          <span className="inline-block px-3 py-1 bg-[#DBEAFE] text-[#1E40AF] text-xs font-bold rounded">D11–D20</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">
                          Implantar revisão amostral (ex.: 10% da carteira/semana) com checklist de erros críticos
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#0F172A]">Revisor Fiscal</td>
                        <td className="px-6 py-4 text-sm text-[#475569]">Resp. Técnico</td>
                        <td className="px-6 py-4">
                          <span className="inline-block px-2 py-1 bg-[#D1FAE5] text-[#065F46] text-xs font-semibold rounded">Semanal</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">Checklist + registro de não conformidades</td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#10B981]">Taxa de retrabalho (↓ semana a semana)</td>
                      </tr>
                      <tr className="hover:bg-[#F8FAFC] transition-colors">
                        <td className="px-6 py-4">
                          <span className="inline-block px-3 py-1 bg-[#DBEAFE] text-[#1E40AF] text-xs font-bold rounded">D21–D30</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">
                          Criar matriz de causa-raiz (top 5 erros) e plano de ação (treino, automação, ajuste processo)
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#0F172A]">Líder Fiscal</td>
                        <td className="px-6 py-4 text-sm text-[#475569]">Sócio</td>
                        <td className="px-6 py-4">
                          <span className="inline-block px-2 py-1 bg-[#E0E7FF] text-[#4338CA] text-xs font-semibold rounded">Quinzenal</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">Planilha + reunião de melhoria</td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#10B981]">Erros críticos por competência (↓)</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* KPIs Recomendados */}
              <div className="bg-gradient-to-br from-[#D1FAE5] to-white border-2 border-[#10B981] rounded-xl p-8">
                <h3 className="text-xl font-bold text-[#0F172A] mb-4 flex items-center gap-3">
                  <i className="ri-bar-chart-box-line text-[#10B981] text-2xl"></i>
                  KPIs Recomendados (Fiscal)
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-[#475569]">
                    <i className="ri-checkbox-circle-fill text-[#10B981] text-xl"></i>
                    <span><strong className="text-[#0F172A]">% entregas no prazo</strong> — Meta inicial: ≥95%</span>
                  </li>
                  <li className="flex items-center gap-3 text-[#475569]">
                    <i className="ri-checkbox-circle-fill text-[#10B981] text-xl"></i>
                    <span><strong className="text-[#0F172A]">Retrabalho por competência</strong> — Acompanhar causa raiz e tendência de redução</span>
                  </li>
                  <li className="flex items-center gap-3 text-[#475569]">
                    <i className="ri-checkbox-circle-fill text-[#10B981] text-xl"></i>
                    <span><strong className="text-[#0F172A]">Tempo de ciclo</strong> — Da entrada de documentos até a entrega final</span>
                  </li>
                </ul>
              </div>

              {/* Tabela 3: Checklist Contábil Detalhado */}
              <div className="mb-12 mt-12">
                <h3 className="text-2xl font-bold text-[#0F172A] mb-6 flex items-center gap-3">
                  <i className="ri-calculator-line text-[#6366F1]"></i>
                  Checklist por Área: Contábil (30 dias)
                </h3>
                <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-lg">
                  <table className="min-w-full bg-white">
                    <thead className="bg-gradient-to-r from-[#6366F1] to-[#4F46E5] text-white">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-bold">Dia (janela)</th>
                        <th className="px-6 py-4 text-left text-sm font-bold">Rotina / Check</th>
                        <th className="px-6 py-4 text-left text-sm font-bold">Responsável (R)</th>
                        <th className="px-6 py-4 text-left text-sm font-bold">Aprovador (A)</th>
                        <th className="px-6 py-4 text-left text-sm font-bold">Frequência</th>
                        <th className="px-6 py-4 text-left text-sm font-bold">Ferramentas</th>
                        <th className="px-6 py-4 text-left text-sm font-bold">KPI Principal</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr className="hover:bg-[#F8FAFC] transition-colors">
                        <td className="px-6 py-4">
                          <span className="inline-block px-3 py-1 bg-[#DBEAFE] text-[#1E40AF] text-xs font-bold rounded">D1–D3</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">
                          Definir padrão de fechamento: conciliações mínimas, evidências e "pronto para assinar"
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#0F172A]">Líder Contábil</td>
                        <td className="px-6 py-4 text-sm text-[#475569]">Resp. Técnico</td>
                        <td className="px-6 py-4">
                          <span className="inline-block px-2 py-1 bg-[#FEF3C7] text-[#92400E] text-xs font-semibold rounded">Única + revisão trimestral</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">SOP + checklist</td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#6366F1]">% fechamentos com checklist completo (≥90%)</td>
                      </tr>
                      <tr className="hover:bg-[#F8FAFC] transition-colors">
                        <td className="px-6 py-4">
                          <span className="inline-block px-3 py-1 bg-[#DBEAFE] text-[#1E40AF] text-xs font-bold rounded">D4–D7</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">
                          Padronizar fluxo de conciliação (bancos, impostos, folha, fornecedores, clientes)
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#0F172A]">Time Contábil</td>
                        <td className="px-6 py-4 text-sm text-[#475569]">Líder Contábil</td>
                        <td className="px-6 py-4">
                          <span className="inline-block px-2 py-1 bg-[#E0E7FF] text-[#4338CA] text-xs font-semibold rounded">Mensal</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">ERP + planilha de conciliação</td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#6366F1]">% conciliações no prazo (≥95%)</td>
                      </tr>
                      <tr className="hover:bg-[#F8FAFC] transition-colors">
                        <td className="px-6 py-4">
                          <span className="inline-block px-3 py-1 bg-[#DBEAFE] text-[#1E40AF] text-xs font-bold rounded">D8–D15</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">
                          Implantar "fila de pendências" por cliente (documentos faltantes, divergências) com SLA
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#0F172A]">Coord. Operações</td>
                        <td className="px-6 py-4 text-sm text-[#475569]">Líder Contábil</td>
                        <td className="px-6 py-4">
                          <span className="inline-block px-2 py-1 bg-[#D1FAE5] text-[#065F46] text-xs font-semibold rounded">Semanal</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">Kanban + templates de cobrança</td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#6366F1]">Tempo médio de pendência (↓)</td>
                      </tr>
                      <tr className="hover:bg-[#F8FAFC] transition-colors">
                        <td className="px-6 py-4">
                          <span className="inline-block px-3 py-1 bg-[#DBEAFE] text-[#1E40AF] text-xs font-bold rounded">D16–D25</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">
                          Revisão por amostragem + auditoria interna leve (1–2 clientes/semana)
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#0F172A]">Revisor</td>
                        <td className="px-6 py-4 text-sm text-[#475569]">Resp. Técnico</td>
                        <td className="px-6 py-4">
                          <span className="inline-block px-2 py-1 bg-[#D1FAE5] text-[#065F46] text-xs font-semibold rounded">Semanal</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">Checklist + registro de achados</td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#6366F1]">Erros críticos contábeis (↓)</td>
                      </tr>
                      <tr className="hover:bg-[#F8FAFC] transition-colors">
                        <td className="px-6 py-4">
                          <span className="inline-block px-3 py-1 bg-[#DBEAFE] text-[#1E40AF] text-xs font-bold rounded">D26–D30</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">
                          Painel de produtividade: carteira x complexidade x horas
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#0F172A]">Coord. Operações</td>
                        <td className="px-6 py-4 text-sm text-[#475569]">Sócio</td>
                        <td className="px-6 py-4">
                          <span className="inline-block px-2 py-1 bg-[#E0E7FF] text-[#4338CA] text-xs font-semibold rounded">Mensal</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">Planilha + dashboard</td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#6366F1]">Capacidade por colaborador (estável)</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* KPIs Recomendados Contábil */}
              <div className="bg-gradient-to-br from-[#E0E7FF] to-white border-2 border-[#6366F1] rounded-xl p-8">
                <h3 className="text-xl font-bold text-[#0F172A] mb-4 flex items-center gap-3">
                  <i className="ri-bar-chart-box-line text-[#6366F1] text-2xl"></i>
                  KPIs Recomendados (Contábil)
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-[#475569]">
                    <i className="ri-checkbox-circle-fill text-[#6366F1] text-xl"></i>
                    <span><strong className="text-[#0F172A]">% fechamentos no prazo</strong> — Meta inicial: ≥95%</span>
                  </li>
                  <li className="flex items-center gap-3 text-[#475569]">
                    <i className="ri-checkbox-circle-fill text-[#6366F1] text-xl"></i>
                    <span><strong className="text-[#0F172A]">Pendências abertas</strong> — Quantidade e idade média</span>
                  </li>
                  <li className="flex items-center gap-3 text-[#475569]">
                    <i className="ri-checkbox-circle-fill text-[#6366F1] text-xl"></i>
                    <span><strong className="text-[#0F172A]">Horas por cliente</strong> — Proxy de margem e eficiência</span>
                  </li>
                  <li className="flex items-center gap-3 text-[#475569]">
                    <i className="ri-checkbox-circle-fill text-[#6366F1] text-xl"></i>
                    <span><strong className="text-[#0F172A]">Erros críticos de fechamento</strong> — Tendência de redução</span>
                  </li>
                </ul>
              </div>

              {/* Tabela 4: Checklist DP Detalhado */}
              <div className="mb-12 mt-12">
                <h3 className="text-2xl font-bold text-[#0F172A] mb-6 flex items-center gap-3">
                  <i className="ri-team-line text-[#F59E0B]"></i>
                  Checklist por Área: DP (Departamento Pessoal / Folha) (30 dias)
                </h3>
                <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-lg">
                  <table className="min-w-full bg-white">
                    <thead className="bg-gradient-to-r from-[#F59E0B] to-[#D97706] text-white">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-bold">Dia (janela)</th>
                        <th className="px-6 py-4 text-left text-sm font-bold">Rotina / Check</th>
                        <th className="px-6 py-4 text-left text-sm font-bold">Responsável (R)</th>
                        <th className="px-6 py-4 text-left text-sm font-bold">Aprovador (A)</th>
                        <th className="px-6 py-4 text-left text-sm font-bold">Frequência</th>
                        <th className="px-6 py-4 text-left text-sm font-bold">Ferramentas</th>
                        <th className="px-6 py-4 text-left text-sm font-bold">KPI Principal</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr className="hover:bg-[#F8FAFC] transition-colors">
                        <td className="px-6 py-4">
                          <span className="inline-block px-3 py-1 bg-[#DBEAFE] text-[#1E40AF] text-xs font-bold rounded">D1–D3</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">
                          Criar calendário DP (admissão/demissão/folha/encargos) + janela de corte por cliente
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#0F172A]">Líder DP</td>
                        <td className="px-6 py-4 text-sm text-[#475569]">Coord. Operações</td>
                        <td className="px-6 py-4">
                          <span className="inline-block px-2 py-1 bg-[#FEF3C7] text-[#92400E] text-xs font-semibold rounded">Única + revisão mensal</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">Planilha calendário + Kanban</td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#F59E0B]">% clientes com janela definida (100%)</td>
                      </tr>
                      <tr className="hover:bg-[#F8FAFC] transition-colors">
                        <td className="px-6 py-4">
                          <span className="inline-block px-3 py-1 bg-[#DBEAFE] text-[#1E40AF] text-xs font-bold rounded">D4–D7</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">
                          Padronizar checklist de admissão/demissão e coleta de documentos
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#0F172A]">Analista DP</td>
                        <td className="px-6 py-4 text-sm text-[#475569]">Líder DP</td>
                        <td className="px-6 py-4">
                          <span className="inline-block px-2 py-1 bg-[#D1FAE5] text-[#065F46] text-xs font-semibold rounded">Sempre que ocorrer</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">SOP + pasta padrão</td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#F59E0B]">Prazo médio de admissão/demissão (↓)</td>
                      </tr>
                      <tr className="hover:bg-[#F8FAFC] transition-colors">
                        <td className="px-6 py-4">
                          <span className="inline-block px-3 py-1 bg-[#DBEAFE] text-[#1E40AF] text-xs font-bold rounded">D8–D15</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">
                          Implementar controle de inputs (ponto, variáveis, benefícios) com SLA de envio do cliente
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#0F172A]">CS/Atendimento + DP</td>
                        <td className="px-6 py-4 text-sm text-[#475569]">Líder DP</td>
                        <td className="px-6 py-4">
                          <span className="inline-block px-2 py-1 bg-[#E0E7FF] text-[#4338CA] text-xs font-semibold rounded">Mensal</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">Helpdesk + templates</td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#F59E0B]">Taxa de variáveis recebidas no prazo (↑)</td>
                      </tr>
                      <tr className="hover:bg-[#F8FAFC] transition-colors">
                        <td className="px-6 py-4">
                          <span className="inline-block px-3 py-1 bg-[#DBEAFE] text-[#1E40AF] text-xs font-bold rounded">D16–D25</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">
                          Revisão dupla (amostral) de folha/encargos antes do envio
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#0F172A]">Revisor DP</td>
                        <td className="px-6 py-4 text-sm text-[#475569]">Líder DP</td>
                        <td className="px-6 py-4">
                          <span className="inline-block px-2 py-1 bg-[#E0E7FF] text-[#4338CA] text-xs font-semibold rounded">Mensal</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">Checklist + trilha de auditoria</td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#F59E0B]">Erros de folha (↓ forte)</td>
                      </tr>
                      <tr className="hover:bg-[#F8FAFC] transition-colors">
                        <td className="px-6 py-4">
                          <span className="inline-block px-3 py-1 bg-[#DBEAFE] text-[#1E40AF] text-xs font-bold rounded">D26–D30</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">
                          Rotina de lições aprendidas (top erros + prevenção)
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#0F172A]">Líder DP</td>
                        <td className="px-6 py-4 text-sm text-[#475569]">Sócio</td>
                        <td className="px-6 py-4">
                          <span className="inline-block px-2 py-1 bg-[#E0E7FF] text-[#4338CA] text-xs font-semibold rounded">Mensal</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#475569]">Reunião + plano de ação</td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#F59E0B]">Retrabalho DP por competência (↓)</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-[#DBEAFE] border-l-4 border-[#1E40AF] p-6 rounded-r-lg mt-8">
                <p className="text-[#1E3A8A] leading-relaxed">
                  <strong className="font-bold">💡 Dica:</strong> Adapte estas tabelas para as áreas Contábil, DP e Atendimento seguindo a mesma estrutura. O importante é ter clareza de responsabilidades, prazos e métricas de sucesso.
                </p>
              </div>
            </div>
          </section>

          {/* Section 8 - FAQ */}
          <section id="faq" className="mb-16 scroll-mt-24">
            <h2 className="text-3xl font-bold text-[#0F172A] mb-6 pb-3 border-b-2 border-[#1E40AF]">
              Perguntas Frequentes (FAQ)
            </h2>
            <div className="space-y-4">
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow">
                <div className="bg-[#F8FAFC] p-5 border-b border-gray-200">
                  <h3 className="text-lg font-bold text-[#0F172A] flex items-start gap-3">
                    <i className="ri-question-line text-[#1E40AF] text-xl flex-shrink-0 mt-1"></i>
                    <span>Por onde começar a organizar um escritório contábil desorganizado?</span>
                  </h3>
                </div>
                <div className="p-5">
                  <p className="text-[#475569] leading-relaxed">
                    Comece pelo diagnóstico: identifique os 3-5 processos que mais geram retrabalho, atraso ou risco. Documente o "jeito certo" de fazer (checklist simples) e defina responsáveis claros. Não tente arrumar tudo de uma vez; priorize o que traz mais impacto imediato.
                  </p>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow">
                <div className="bg-[#F8FAFC] p-5 border-b border-gray-200">
                  <h3 className="text-lg font-bold text-[#0F172A] flex items-start gap-3">
                    <i className="ri-question-line text-[#1E40AF] text-xl flex-shrink-0 mt-1"></i>
                    <span>Quais KPIs são essenciais para um escritório contábil?</span>
                  </h3>
                </div>
                <div className="p-5">
                  <p className="text-[#475569] leading-relaxed">
                    Os mais úteis são: % de entregas no prazo (por área), quantidade de retrabalho por competência, tempo médio de atendimento (SLA), capacidade por colaborador e erros críticos. Evite medir tudo; escolha 3-5 indicadores que realmente guiam decisões.
                  </p>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow">
                <div className="bg-[#F8FAFC] p-5 border-b border-gray-200">
                  <h3 className="text-lg font-bold text-[#0F172A] flex items-start gap-3">
                    <i className="ri-question-line text-[#1E40AF] text-xl flex-shrink-0 mt-1"></i>
                    <span>Como reduzir a dependência de "pessoas-chave" no escritório?</span>
                  </h3>
                </div>
                <div className="p-5">
                  <p className="text-[#475569] leading-relaxed">
                    Documente processos em checklists e modelos, defina padrões de qualidade claros e crie trilhas de treinamento por nível (júnior, pleno, sênior). Isso permite que qualquer pessoa treinada execute com qualidade, sem depender de conhecimento tácito de poucos.
                  </p>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow">
                <div className="bg-[#F8FAFC] p-5 border-b border-gray-200">
                  <h3 className="text-lg font-bold text-[#0F172A] flex items-start gap-3">
                    <i className="ri-question-line text-[#1E40AF] text-xl flex-shrink-0 mt-1"></i>
                    <span>O que é BPM e por que é importante para escritórios contábeis?</span>
                  </h3>
                </div>
                <div className="p-5">
                  <p className="text-[#475569] leading-relaxed">
                    BPM (Business Process Management) é a gestão de processos: identificar, documentar, monitorar e melhorar continuamente as rotinas do escritório. É importante porque contabilidade é serviço recorrente e regulado — padronização reduz erros, acelera entregas e facilita crescimento.
                  </p>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow">
                <div className="bg-[#F8FAFC] p-5 border-b border-gray-200">
                  <h3 className="text-lg font-bold text-[#0F172A] flex items-start gap-3">
                    <i className="ri-question-line text-[#1E40AF] text-xl flex-shrink-0 mt-1"></i>
                    <span>Como garantir segurança da informação e conformidade com LGPD?</span>
                  </h3>
                </div>
                <div className="p-5">
                  <p className="text-[#475569] leading-relaxed">
                    Implemente controle de acessos por perfil, padronize armazenamento de documentos, mantenha rotina de backup, treine o time em boas práticas de segurança e documente política interna de uso de dados. LGPD exige organização e processos, não apenas tecnologia.
                  </p>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow">
                <div className="bg-[#F8FAFC] p-5 border-b border-gray-200">
                  <h3 className="text-lg font-bold text-[#0F172A] flex items-start gap-3">
                    <i className="ri-question-line text-[#1E40AF] text-xl flex-shrink-0 mt-1"></i>
                    <span>Qual a diferença entre escritório "que trabalha muito" e "bem administrado"?</span>
                  </h3>
                </div>
                <div className="p-5">
                  <p className="text-[#475569] leading-relaxed">
                    Escritório que "trabalha muito" depende de heróis apagando incêndio, tem retrabalho constante e imprevisibilidade. Escritório "bem administrado" entrega com qualidade, prazo e margem consistentes, usando processos claros, métricas e melhoria contínua — sem depender de pessoas específicas.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <div className="bg-gradient-to-br from-[#1E40AF] to-[#1E3A8A] rounded-2xl p-10 text-center text-white mt-16">
            <div className="max-w-2xl mx-auto">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-lightbulb-line text-4xl"></i>
              </div>
              <h2 className="text-3xl font-bold mb-4">
                Precisa de ajuda para organizar seu escritório contábil?
              </h2>
              <p className="text-lg text-white/90 mb-8 leading-relaxed">
                Oferecemos diagnóstico gratuito para identificar gargalos, priorizar processos e criar um plano de ação personalizado para seu escritório.
              </p>
              <Link
                to="/contato"
                className="inline-flex items-center gap-3 bg-white text-[#1E40AF] px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-mail-line text-xl"></i>
                Solicitar Diagnóstico Gratuito
              </Link>
            </div>
          </div>

          {/* Related Articles */}
          <div className="mt-16 pt-12 border-t-2 border-gray-200">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-8 flex items-center gap-3">
              <i className="ri-article-line text-[#1E40AF]"></i>
              Artigos Relacionados
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Link 
                to="/artigo/cbs-ibs-nova-emissao-nota-fiscal-reforma-tributaria"
                className="group bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-xl transition-all"
              >
                <div className="p-6">
                  <span className="inline-block px-3 py-1 bg-[#1E40AF] text-white text-xs font-semibold rounded mb-3 uppercase tracking-wide">
                    Reforma Tributária
                  </span>
                  <h3 className="text-lg font-bold text-[#0F172A] mb-2 group-hover:text-[#1E40AF] transition-colors">
                    CBS e IBS: Nova Emissão de Nota Fiscal na Reforma Tributária
                  </h3>
                  <p className="text-sm text-[#64748B] line-clamp-2">
                    Entenda as mudanças na emissão de documentos fiscais eletrônicos a partir de 2026
                  </p>
                </div>
              </Link>
              <Link 
                to="/categoria/ativo-fixo-patrimonio"
                className="group bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-xl transition-all"
              >
                <div className="p-6">
                  <span className="inline-block px-3 py-1 bg-[#10B981] text-white text-xs font-semibold rounded mb-3 uppercase tracking-wide">
                    Categoria
                  </span>
                  <h3 className="text-lg font-bold text-[#0F172A] mb-2 group-hover:text-[#10B981] transition-colors">
                    Ver todos os artigos de Ativo Fixo &amp; Patrimônio
                  </h3>
                  <p className="text-sm text-[#64748B] line-clamp-2">
                    Explore mais conteúdos sobre gestão patrimonial e ativo fixo
                  </p>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}
